import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  createNewsArticle,
  getAllNewsArticles,
  updateNewsArticle,
  deleteNewsArticle,
  getNewsArticle,
} from "../../firebase";
import { NewsArticle } from "../../types";
import { format } from "date-fns";
import RichTextEditor from "../../components/RichTextEditor";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CloudArrowUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Format date helper
const formatDate = (timestamp: number) => {
  return format(new Date(timestamp), "MMM d, yyyy h:mm a");
};

const NewsManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null);

  // Form states
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  // Load news articles on component mount
  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoadingArticles(true);
      const result = await getAllNewsArticles();
      setArticles(result);
    } catch (error: any) {
      console.error("Error loading news articles:", error);
      showNotification(
        `Failed to load news articles: ${error.message}`,
        "error"
      );
    } finally {
      setLoadingArticles(false);
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImage(file);

      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setImage(null);
    setImagePreview(null);
    setPublished(false);
    setEditMode(false);
    setCurrentArticleId(null);
  };

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({
      show: true,
      message,
      type,
    });

    // Auto hide after 5 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showNotification("Please enter a title", "error");
      return;
    }

    try {
      setLoading(true);

      if (editMode && currentArticleId) {
        // Update existing news article
        await updateNewsArticle(
          currentArticleId,
          {
            title: title.trim(),
            body,
            published,
          },
          image
        );

        showNotification("News article updated successfully!", "success");
      } else {
        // Create new news article
        await createNewsArticle(
          title.trim(),
          body,
          image,
          currentUser?.uid || "unknown",
          published
        );

        showNotification("News article created successfully!", "success");
      }

      // Reset form and refresh articles
      resetForm();
      fetchArticles();

      // Switch to articles list tab
      setTabValue(1);
    } catch (error: any) {
      console.error("Error saving news article:", error);
      showNotification(
        `Failed to save news article: ${error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditArticle = async (articleId: string) => {
    try {
      setLoading(true);
      const article = await getNewsArticle(articleId);

      // Populate form with article data
      setTitle(article.title);
      setBody(article.body);
      setImagePreview(article.imageURL || null);
      setPublished(article.published);
      setEditMode(true);
      setCurrentArticleId(articleId);

      // Switch to edit tab
      setTabValue(0);
    } catch (error: any) {
      console.error("Error loading news article for edit:", error);
      showNotification(
        `Failed to load news article: ${error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (articleId: string) => {
    setArticleToDelete(articleId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleDeleteArticle = async () => {
    if (!articleToDelete) return;

    try {
      setLoading(true);
      await deleteNewsArticle(articleToDelete);

      // Update local state to reflect deletion
      setArticles((prevArticles) =>
        prevArticles.filter((article) => article.id !== articleToDelete)
      );

      showNotification("News article deleted successfully", "success");
    } catch (error: any) {
      console.error("Error deleting news article:", error);
      showNotification(
        `Failed to delete news article: ${error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
      closeDeleteDialog();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              tabValue === 0
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleTabChange(0)}
          >
            {editMode ? "Edit News Article" : "Create News Article"}
          </button>
          <button
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              tabValue === 1
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleTabChange(1)}
          >
            News Articles
          </button>
        </nav>
      </div>

      {/* Create/Edit News Article Tab */}
      <div className={`p-6 ${tabValue !== 0 ? "hidden" : ""}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editMode ? "Edit News Article" : "Create New News Article"}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-12">
              {/* Title */}
              <div className="mb-4">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  News Article Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
                  placeholder="Enter title"
                  required
                  disabled={loading}
                />
              </div>


{/* Image Upload */}
<div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Featured Image
                </label>
                <div
                  className="w-full h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer relative overflow-hidden mb-2"
                  onClick={handleImageClick}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="News article preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <CloudArrowUpIcon className="h-10 w-10 text-gray-400" />
                      <span className="text-gray-500">
                        Click to upload image
                      </span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Recommended image size: 1200x628 pixels (16:9 ratio)
                </p>
              </div>


              {/* Rich Text Editor */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Enter content here..."
                  readOnly={loading}
                />
              </div>

              {/* Publish Switch */}
              <div className="flex items-center mt-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={published}
                    onChange={(e) => setPublished(e.target.checked)}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-700">
                    Publish News Article
                  </span>
                </label>
              </div>

              
            </div>
          </div>

          {/* Submit/Cancel Buttons */}
          <div className="mt-6 flex justify-end">
            {editMode && (
              <button
                type="button"
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded mr-2"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className={`bg-gold text-white px-4 py-2 rounded ${
                loading ? "cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : editMode
                ? "Update News Article"
                : "Create News Article"}
            </button>
          </div>
        </form>
      </div>

      {/* News Articles Tab */}
      <div className={`p-6 ${tabValue !== 1 ? "hidden" : ""}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">News Articles</h2>
          <button
            className="bg-gold text-white px-4 py-2 rounded"
            onClick={() => {
              resetForm();
              setTabValue(0);
            }}
          >
            New Article
          </button>
        </div>

        {loadingArticles ? (
          <div className="flex justify-center items-center h-64">
            <div className="border-4 border-t-4 border-gray-300 rounded-full w-12 h-12 animate-spin"></div>
          </div>
        ) : articles.length === 0 ? (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <p className="text-gray-500">No news articles found</p>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 px-4 text-left">Title</th>
                  <th className="py-3 px-4 text-left">Date</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        {article.imageURL && (
                          <img
                            src={article.imageURL}
                            alt={article.title}
                            className="w-10 h-10 object-cover rounded mr-3"
                          />
                        )}
                        <span className="text-gray-700">{article.title}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {formatDate(article.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      {article.published ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                          Published
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-500 text-white px-2 py-1 rounded"
                          onClick={() => handleEditArticle(article.id)}
                          disabled={loading}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="bg-red-500 text-white px-2 py-1 rounded"
                          onClick={() => openDeleteDialog(article.id)}
                          disabled={loading}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <div
        className={`fixed inset-0 flex items-center justify-center ${
          !deleteDialogOpen ? "hidden" : ""
        }`}
      >
        <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Confirm Delete
            </h2>
            <button
              className="bg-gray-200 text-gray-700 px-2 py-1 rounded"
              onClick={closeDeleteDialog}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-700 mb-4">
            Are you sure you want to delete this news article? This action
            cannot be undone.
          </p>
          <div className="flex justify-end">
            <button
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded mr-2"
              onClick={closeDeleteDialog}
            >
              Cancel
            </button>
            <button
              className="bg-red-500 text-white px-4 py-2 rounded"
              onClick={handleDeleteArticle}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed bottom-4 right-4 w-full max-w-sm ${
            notification.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          } p-4 rounded-lg shadow-md`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {notification.type === "success" ? (
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-1.414-1.414zm-5.414 0a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-1.414-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-1.414-1.414zm-5.414 0a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-1.414-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
